import React, { useState, useContext, ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Typography,
  Box,
  FormControl,
  CircularProgress,
  Container,
} from '@mui/material';
import StyledButton from '../../../components/Button/Button';
import EmailInput from '../../../components/Inputs/EmailInput';
import PasswordInput from '../../../components/Inputs/PasswordInput';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import TopNav from '../../../components/TopNav/TopNav';
import { register } from '../../../services/NetworkServices';
import { SignupResponse } from '../../../services/types/APIResponseType';
import {
  getRole,
  storeApprovalStatus,
  storeEmail,
  storeStageNumber,
  storeToken,
} from '../../../utils/auth-token-utils/cookie';
import { SUPERVISOR_SESSION_MARKER } from '../../../hooks/useSupervisorAutoLogout';
import ApiStatus from '../../../config/apiStatusConstants';
import { AuthContext } from '../../../context/AuthContext';
import './Signup.css';
import { SignupRequestPayload } from '../../../services/types/APIRequestType';

const Signup: React.FC = () => {
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('Auth Context must be used within  AuthProvider');
  }
  const { email, password, handleEmail, handlePassword, fcmToken } =
    authContext;

  const navigate = useNavigate();

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    handleEmail(event.target.value);
  };

  const onChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
    handlePassword(event.target.value);
  };

  const onChangeConfirmPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
  };

  const onChecked = () => {
    setIsChecked((prevIsChecked) => !prevIsChecked);
  };

  const role = getRole() || '';

  const handleSignup = async () => {
    setApiStatus(ApiStatus.LOADING);
    try {
      const payload: SignupRequestPayload = {
        email: email ?? '',
        password: password ?? '',
        confirmPassword: confirmPassword,
        role: role ?? '',
      };

      if (fcmToken) {
        payload.fcmToken = [fcmToken];
      }
      const response = await register(payload);
      const data: SignupResponse = await response.data;
      setApiStatus(ApiStatus.SUCCESS);

      if (data) {
        storeToken(data.data.userTokenSaving.token);
        storeEmail(email);
        if (role === 'Supervisor') {
          sessionStorage.setItem(SUPERVISOR_SESSION_MARKER, '1');
          storeApprovalStatus('pending');
          storeStageNumber('1');
          navigate('/babysitter/createProfile');
        }
        if (role === 'Participant') {
          navigate('/participant/createProfile');
        }
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      setErrorMsg(
        error.response.data.message ||
          'Something went wrong. Please try again.',
      );
    }
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton>
          <CircularProgress size={20} />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton
          onClick={handleSignup}
          disabled={!(email && password && confirmPassword && isChecked)}
        >
          Sign Up
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = () => {
    if (apiStatus === ApiStatus.FAILURE) {
      return <Typography className="error1-msg">{errorMsg}</Typography>;
    }
    return null;
  };

  return (
    <StyledBgContainer>
      <TopNav showArrow={true} backRoute={'/'} />
      <Container maxWidth="sm">
        <Box component="form" autoComplete="new-password">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { sm: 6 },
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: 'white', fontFamily: 'Opensans', fontWeight: '700' }}
            >
              Sign Up
            </Typography>
            <Typography variant="body2" sx={{ color: 'grey', mt: 1, mb: 3 }}>
              Enter your Email address and create a password to access the web
              platform and mobile appplication.
            </Typography>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mb: 1, alignItems: 'center' }}
            >
              <EmailInput
                type="email"
                onChange={onChangeEmail}
                value={email}
                label="Email Address"
                placeholder="Enter your email"
              />
            </FormControl>
            {(errorMsg === 'Invalid email format' ||
              errorMsg === 'User exists') && (
              <Box
                className="signup-error"
                sx={{ marginTop: '8px', mb: 1, pl: 2 }}
              >
                {renderErrorMsg()}
              </Box>
            )}
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mb: 1, alignItems: 'center' }}
            >
              <PasswordInput
                onChange={onChangePassword}
                value={password}
                label="Set Password"
              />
              {(errorMsg.includes('uppercase') ||
                errorMsg === 'Password should be at least 8 characters long' ||
                errorMsg ===
                  'Password should be no more than 18 characters long') && (
                <Box
                  className="signup-error"
                  sx={{ marginTop: '8px', mb: 1, pl: 2, width: '100%' }}
                >
                  {renderErrorMsg()}
                </Box>
              )}
            </FormControl>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mb: 1, alignItems: 'center' }}
            >
              <PasswordInput
                onChange={onChangeConfirmPassword}
                value={confirmPassword}
                label="Retype Password"
              />
            </FormControl>

            {errorMsg.includes('Password should match') && (
              <Box
                className="signup-error"
                sx={{ marginTop: '8px', mb: 1, pl: 2 }}
              >
                {renderErrorMsg()}
              </Box>
            )}
            <FormControl sx={{ mb: 1 }}>
              <Box
                mt={2}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  color: 'grey',
                  pl: 1.5,
                  gap: 1,
                }}
              >
                <Box sx={{ width: '20px', height: '20px' }}>
                  <input
                    type="checkbox"
                    id="checkbox"
                    onChange={onChecked}
                    checked={isChecked}
                    className="checkbox"
                  />
                </Box>
                <label htmlFor="checkbox">
                  <Typography
                    sx={{
                      fontSize: { xs: '13px', sm: '13px' },
                      textAlign: 'left',
                    }}
                  >
                    I acknowledge that I have read and understand the
                    <Link
                      to={
                        role === 'Supervisor'
                          ? '/supervisor/terms'
                          : '/participant/terms'
                      }
                      className="linkStyle"
                    >
                      {' '}
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to={
                        role === 'Supervisor'
                          ? '/supervisor/privacy'
                          : '/participant/privacy'
                      }
                      className="linkStyle"
                    >
                      Privacy Policy&nbsp;
                    </Link>
                    set forth herein.
                  </Typography>
                </label>
              </Box>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ mt: 5 }}>{renderButton()}</Box>
        <Box sx={{ mt: 3, mb: 6 }}>
          <Typography variant="body1" sx={{ color: 'grey', mb: 3 }}>
            Already have an account ?{' '}
            <Link to="/signin" className="linkStyle">
              Sign In
            </Link>
          </Typography>
        </Box>
      </Container>
    </StyledBgContainer>
  );
};

export default Signup;
